import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { interval, Subscription } from 'rxjs';
import { finalize, take } from 'rxjs/operators';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import { MatDialog } from '@angular/material/dialog';
import { I18NextPipe } from 'angular-i18next';
import { DialogHelpComponent } from 'src/app/common/dialogs/dialog-help/dialog-help.component';
import { realTimeService } from 'src/app/services/services/realtime.service';
import { ProfileDialogComponent } from '../../real-time-dialogs/profile-dialog/profile-dialog.component';
import { ContactCentreStatus, statusKey } from 'src/app/shared/utils/status-utils';
import { SettingsService } from 'src/app/services/services/settings.service';
import { SetLogOnDialogComponent } from '../../real-time-dialogs/set-logOn-dialog/set-logOn-dialog.component';
import { SetPauseDialogComponent } from '../../real-time-dialogs/set-pause-dialog/set-pause-dialog.component';

export interface AgentOverviewRow {
  name: string;
  available: number;
  connecting: number;
  engaged: number;
  unavailable: number;
  wrapup: number;
  noanswer: number;
  pause: number;
  loggedoff: number;
}

export interface AgentDetailRow {
  userId: number;
  userOwned: 0 | 1;
  faded?: boolean;

  formattedName: string;
  name: string;
  formattedPhoneNumber?: string;
  phoneNumber?: string | null;

  profileName: string;

  statusIcon?: string;
  statusColor?: string;
  formattedStatus: string;
  numericStatusValue: number;
  secondsInStatus: number;
  formattedSecondsInStatus: string;

  linkTypes?: Array<'logOn' | 'setPause' | 'setReady' | 'logOff' | 'changePause'>;
}

function pick<T = any>(obj: any, keys: string[], def?: T): T {
  for (const k of keys) {
    if (obj && obj[k] != null) return obj[k];
  }
  return def as T;
}

function statusOrderFromId(id?: number): number {
  switch (id) {
    case 4: return 10; // Available
    case 5: return 20; // Connected
    case 3: return 30; // Engaged
    case 2: return 40; // Pause
    case 6: return 50; // Wrap-up
    case 7: return 60; // No answer
    case 1: return 70; // Logged off
    default: return 99;
  }
}
function formatSeconds(s?: number | null): string {
  if (s == null || isNaN(s as any)) return '—';
  const n = Math.max(0, Math.round(s));
  const h = Math.floor(n / 3600);
  const m = Math.floor((n % 3600) / 60);
  const ss = n % 60;
  if (h > 0) return `${h}h ${m}m ${ss}s`;
  if (m > 0) return `${m}m ${ss}s`;
  return `${ss}s`;
}

@Component({
    selector: 'app-real-time-overview-agent',
    templateUrl: './real-time-overview-agent.component.html',
    styleUrls: ['./real-time-overview-agent.component.scss'],
    standalone: false
})
export class RealTimeOverviewAgentComponent implements OnInit, OnDestroy {
  id: number | null = null;
  queueDescription = '—';

  canChangeAgentStatus = true;

  overviewDisplayedColumns = [
    'name', 'available', 'connecting', 'engaged', 'unavailable', 'wrapup', 'noanswer', 'pause', 'loggedoff'
  ];
  overviewDS = new MatTableDataSource<AgentOverviewRow>([]);

  detailsDisplayedColumns = [
    'agent', 'phone', 'profile', 'status', 'timeInStatus', 'actions'
  ];
  detailsDS = new MatTableDataSource<AgentDetailRow>([]);

  BreadcrumbsLink = [];

  @ViewChild(MatSort, { static: true }) sort!: MatSort;

  filter = '';
  isLoading = true;

  pollingIntervalMs = 10000;
  private pollingSub?: Subscription;

  constructor(
    private route: ActivatedRoute,
    private dialog: MatDialog,
    private i18n: I18NextPipe,
    private realTimeService: realTimeService,
    private settingsService: SettingsService
  ) { }

  async ngOnInit(): Promise<void> {
    this.id = Number(this.route.snapshot.params.id ?? null);

    this.realTimeService.GetQueueName(this.id!)
      .pipe(take(1))
      .subscribe(name => {
        this.queueDescription = name || '—';

        this.BreadcrumbsLink = [
          { "label": "Home", "url": "/" },
          { "label": "Real-Time", "url": "/real-time/overview" },
          { "label": "Queue Overview", "url": "/real-time/overview" },
          { "label": name, "url": `/real-time/overview/agent/${this.id}` }];
      });

    this.detailsDS.filterPredicate = (row, filterText) => {
      const f = (filterText || '').trim().toLowerCase();
      if (!f) return true;
      return [
        row.formattedName,
        row.name,
        row.profileName,
        row.formattedStatus,
        row.formattedPhoneNumber
      ].some(v => (v || '').toLowerCase().includes(f));
    };

    this.detailsDS.sortingDataAccessor = (row: AgentDetailRow, column: string) => {
      switch (column) {
        case 'agent': return (row.formattedName || '').toLowerCase();
        case 'phone': return (row.formattedPhoneNumber || row.phoneNumber || '').toLowerCase();
        case 'profile': return (row.profileName || '').toLowerCase();
        case 'status': return row.numericStatusValue ?? 999;
        case 'timeInStatus': return row.secondsInStatus ?? -1;
        default: return '';
      }
    };
    this.detailsDS.sort = this.sort;

    // --- Pull realtime settings from SettingsService (/Settings/RealtimeSettings)
    const cfg: any = await this.settingsService.realtimeSettings();
    this.canChangeAgentStatus = !!cfg?.canChangeAgentStatus;
    this.pollingIntervalMs = this.normalizeInterval(cfg?.ajaxRefreshInterval);

    this.loadData(true);
    this.startPolling();
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }

  // ---------- Data load ----------
  private loadData(showLoader = false): void {
    if (showLoader) this.isLoading = true;

    this.realTimeService.GetAgentDetailsForQueue(this.id!)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe(payload => {
        const root: any = payload || {};
        const overview = root.overview ?? root.Overview ?? [];
        const details = root.details ?? root.Details ?? [];

        // ---- Overview (case-insensitive) ----
        const ov: AgentOverviewRow[] = (overview || []).map((o: any) => ({
          name: pick(o, ['name', 'Name'], '—'),
          available: pick(o, ['available', 'Available'], 0),
          connecting: pick(o, ['connecting', 'Connecting'], 0),
          engaged: pick(o, ['engaged', 'Engaged'], 0),
          unavailable: pick(o, ['unavailable', 'Unavailable'], 0),
          wrapup: pick(o, ['wrapup', 'Wrapup'], 0),
          noanswer: pick(o, ['noanswer', 'Noanswer'], 0),
          pause: pick(o, ['pause', 'Pause'], 0),
          loggedoff: pick(o, ['loggedoff', 'Loggedoff'], 0),
        }));
        this.overviewDS.data = ov;

        // ---- Details (case-insensitive) ----
        const det: AgentDetailRow[] = (details || []).map((d: any) => {

          const formattedName = pick<string>(d, ['formattedName', 'FormattedName'], '');
          const firstName = pick<string>(d, ['firstName', 'FirstName'], '');
          const lastName = pick<string>(d, ['lastName', 'LastName'], '');
          const name = pick<string>(d, ['name', 'Name'], '—');
          const userId = pick<number>(d, ['userId', 'UserId']);
          const userOwned = (pick<number>(d, ['userOwned', 'UserOwned'], 0) as 0 | 1);
          const faded = !!pick<boolean>(d, ['faded', 'Faded'], false);

          const statusId = pick<number>(d, ['userStatusId', 'UserStatusId', 'simpleStatus', 'SimpleStatus']);
          const rawStatus = pick<string>(d, ['formattedStatus', 'FormattedStatus'], '');
          const statusIcon = pick<string>(d, ['statusIcon', 'StatusIcon']);
          const statusColor = pick<string>(d, ['statusColor', 'StatusColor']);
          const statusOrd = pick<number>(d, ['numericStatusValue', 'NumericStatusValue'], statusOrderFromId(statusId));
          const contactCentreStatus = pick<number>(d, ['contactCentreStatus', 'ContactCentreStatus']);

          const suffixFromRaw = this.getLastOuterParens(rawStatus) || '';
          const chats = pick<number>(d, ['chatsOnGoing', 'ChatsOnGoing'], undefined);
          const suffix = suffixFromRaw || (typeof chats === 'number' ? `(${chats})` : '');

          const key = statusKey( (contactCentreStatus ?? ContactCentreStatus.ReadyOrBusy), // <- prevent "Logged off" default
            statusId);

          let formattedStatus = rawStatus;
            if (key !== 'NotFound') {
              const translatedLabel = this.i18n.transform(key);
               formattedStatus = `${translatedLabel}${suffix ? ' ' + suffix : ''}`;
            }

          const secsIn = pick<number>(d, ['secondsInStatus', 'SecondsInStatus'], 0);
          const phone = pick<string | null>(d, ['phoneNumber', 'PhoneNumber'], null);
          const phoneFmt = pick<string>(d, ['formattedPhoneNumber', 'FormattedPhoneNumber'], phone ?? '—');
          const profile = pick<string>(d, ['profileName', 'ProfileName'], '—');

          // Prefer SimpleStatus if present, fall back to statusId
          const simpleStatus = pick<number>(d, ['simpleStatus', 'SimpleStatus'], statusId);

          let linkTypes: AgentDetailRow['linkTypes'] = [];

          if (userOwned === 1) {
            // Ready/Busy group (same as the old C# switch)
            const readyOrBusyStatuses = [2, 3, 5, 9, 11, 10];
            // Available=2, Allocated=3, Connected=5, Busy=9, WrapUp=11, NoAnswer=10

            if (readyOrBusyStatuses.includes(simpleStatus)) {
              // Agent is "working" → Pause them, change pause, or log off
              linkTypes = ['setPause', 'logOff'];
            }
            else if (simpleStatus === 8) {
              // Pause = 8 → Set Ready, Change Pause, Log off (NO Set pause here)
              linkTypes = ['setReady', 'changePause', 'logOff'];
            }
            else {
              // LoggedOff and all other states → only Log on
              linkTypes = ['logOn'];
            }
          }

          return {
            userId,
            userOwned,
            faded,
            formattedName: formattedName.trim() || (firstName + ' ' + lastName).trim() || name,
            name,
            formattedPhoneNumber: phoneFmt,
            phoneNumber: phone,
            profileName: profile,
            statusIcon,
            statusColor,
            formattedStatus,
            numericStatusValue: statusOrd,
            secondsInStatus: secsIn,
            formattedSecondsInStatus: formatSeconds(secsIn),
            linkTypes
          };
        });


        this.detailsDS.data = det;
      });
  }

  private getLastOuterParens(str): string {
  let depth = 0;
  let end = -1;

  for (let i = str.length - 1; i >= 0; i--) {
    if (str[i] === ')') {
      if (depth === 0) end = i;
      depth++;
    } else if (str[i] === '(') {
      depth--;
      if (depth === 0 && end !== -1) {
        return str.slice(i, end + 1);
      }
    }
  }
  return '';
}

  private startPolling(): void {
    this.stopPolling();
    this.pollingSub = interval(this.pollingIntervalMs).subscribe(() => this.loadData(false));
  }
  private stopPolling(): void {
    this.pollingSub?.unsubscribe();
    this.pollingSub = undefined;
  }

  private normalizeInterval(raw: unknown): number {
    let ms = Number(raw) || 10000;          // default 10s
    if (ms > 0 && ms <= 60) ms *= 1000;     // treat small numbers as seconds
    return Math.max(1000, ms);              // clamp to >= 1s
  }

  // ---------- Filter ----------
  applyFilterFromProperty() {
    this.detailsDS.filter = (this.filter || '').trim().toLowerCase();
  }
  clearFilter() {
    this.filter = '';
    this.detailsDS.filter = '';
  }

  // ---------- Actions ----------
  onViewProfile(row: AgentDetailRow): void {
    this.dialog.open(ProfileDialogComponent, {
      width: '720px',
      maxHeight: '80vh',
      data: { userId: row.userId, displayName: row.formattedName }
    });
  }

  onSetReady(row: AgentDetailRow): void {
    if (!this.canChangeAgentStatus || row.userOwned !== 1) return;
    this.isLoading = true;
    this.realTimeService.SetUserAvailable(row.userId)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe(() => this.loadData(false));
  }

 onSetPause(row: AgentDetailRow, pauseId?: number): void {
  if (!this.canChangeAgentStatus || row.userOwned !== 1) return;

  const doCall = (idOrNull: number | null) => {
    this.isLoading = true;
    this.realTimeService
      .SetAgentPause(row.userId, idOrNull as any)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe(() => this.loadData(false));
  };

  // Keep explicit pause id support (programmatic usage)
  if (typeof pauseId === 'number') {
    doCall(pauseId);
    return;
  }

  // 🔹 NEW: open pause dialog and use its result
  const dialogRef = this.dialog.open(SetPauseDialogComponent, {
    width: '600px',
    // optional, only if your dialog needs it:
    data: { userId: row.userId, phoneNumber: row.phoneNumber }
  });

  dialogRef.afterClosed().pipe(take(1)).subscribe(result => {
    if (!result && result !== 0) {
      // dialog cancelled or no selection
      return;
    }

    // In your other component, `result` is the pauseId
    doCall(result);
  });
}

  onChangePause(row: AgentDetailRow, pauseId?: number): void {
    this.onSetPause(row, pauseId);
  }

onLogOn(
  row: AgentDetailRow,
  opts?: { phoneNumber: string; profileId: number; profileNumber: number }
): void {
  if (!this.canChangeAgentStatus || row.userOwned !== 1) return;

  const doCall = (phone: string, profileId: number, profileNumber: number) => {
    this.isLoading = true;
    this.realTimeService
      .SetUserLogOn(phone, row.userId, profileId, profileNumber)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe(() => this.loadData(false));
  };

  // Keep support for direct programmatic usage (optional)
  if (opts?.phoneNumber && opts?.profileId && opts?.profileNumber) {
    doCall(opts.phoneNumber, opts.profileId, opts.profileNumber);
    return;
  }

  // 🔹 NEW: open the same dialog as in the other component
  const dialogRef = this.dialog.open(SetLogOnDialogComponent, {
    data: {
      userId: row.userId,
      phoneNumber: row.phoneNumber != 'Softphone' ? row.phoneNumber : '', // or '' if you prefer forcing input
    },
    width: '600px'
  });

  dialogRef.afterClosed().pipe(take(1)).subscribe(result => {
    if (!result) return; // dialog cancelled

    // result is expected to contain: phoneNumber, profileId, profileNumber
    doCall(result.phoneNumber, result.profileId, result.profileNumber);
  });
}


  onLogOff(row: AgentDetailRow): void {
    if (!this.canChangeAgentStatus) return;
    this.isLoading = true;
    this.realTimeService.SetUserLogOff(row.userId)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe(() => this.loadData(false));
  }

  // ---------- Help ----------
  openHelpOverview(): void {
    this.dialog.open(DialogHelpComponent, {
      panelClass: 'dialog-help-pannel',
      backdropClass: 'backdrop-invisible',
      width: '600px',
      data: {
        title: this.i18n.transform('realtime:help_title_queueAgentOverview'),
        text: this.i18n.transform('realtime:help_queueAgentOverview'),
        renderAsHtml: true
      }
    });
  }

  getActions(r: AgentDetailRow): { type: string, label: string, click: () => void }[] {
    if (r.userOwned !== 1 || !r.linkTypes) return [];

    const actions: any[] = [];

    if (r.linkTypes.includes('logOn')) {
      actions.push({
        type: 'logOn',
        label: 'realtime:usergroups_lang_logOn',
        click: () => this.onLogOn(r)
      });
    }

    if (r.linkTypes.includes('setPause')) {
      actions.push({
        type: 'setPause',
        label: 'realtime:usergroups_lang_setPause',
        click: () => this.onSetPause(r)
      });
    }

    if (r.linkTypes.includes('setReady')) {
      actions.push({
        type: 'setReady',
        label: 'realtime:usergroups_lang_setReady',
        click: () => this.onSetReady(r)
      });
    }

    if (r.linkTypes.includes('changePause')) {
      actions.push({
        type: 'changePause',
        label: 'realtime:usergroups_lang_changePause',
        click: () => this.onChangePause(r)
      });
    }

    if (r.linkTypes.includes('logOff')) {
      actions.push({
        type: 'logOff',
        label: 'realtime:usergroups_lang_logOff',
        click: () => this.onLogOff(r)
      });
    }

    return actions;
  }


  openHelpDetails(): void {
    this.dialog.open(DialogHelpComponent, {
      panelClass: 'dialog-help-pannel',
      backdropClass: 'backdrop-invisible',
      width: '600px',
      data: {
        title: this.i18n.transform('realtime:help_title_queueAgentDetails'),
        text: this.i18n.transform('realtime:help_queueAgentDetails'),
        renderAsHtml: true
      }
    });
  }
}
